import { useCallback, useMemo } from 'react';
import { Box, Text } from '@nimbus-ds/components';
import { InvoiceIcon } from '@nimbus-ds/icons';
import { ConceptCode, PaymentBillingEngineDto } from '@tiendanube/common';
import { CurrencyPrice } from 'commons/components';
import { dateFormat, Format } from 'commons/utils/date';
import { useGetDownloadInvoiceMutation } from 'domains/Billing/Checkout/CheckoutApi';
import { useFrequencyTranslation } from 'domains/Billing/Checkout/hooks';
import useTranslatePaymentMethod from 'domains/Billing/Checkout/hooks/useTranslatePaymentMethod';
import { isAppRelatedConcept } from 'domains/Billing/Checkout/pages/utils';
import useTranslationBilling from 'domains/Billing/useTranslationBilling';
import PaymentHistory from '../../PaymentHistory';

interface PaymentDataProps {
  concept: ConceptCode;
  appId?: string;
  payment: PaymentBillingEngineDto;
}

export default function PaymentData({
  payment: { charges, paidAt, amountCurrency, gatewayDetail },
  concept,
  appId,
}: Readonly<PaymentDataProps>) {
  const t = useTranslationBilling(
    'checkout.conceptDetailContent.paymentHistory',
  );
  const relevantCharges = useMemo(() => {
    const isAppRelated = isAppRelatedConcept(concept);
    const isPlanRelated = concept === 'plan-cost';

    return charges.filter(
      (charge) =>
        (charge.concept === concept ||
          (isAppRelated && charge.concept === 'app-extra-fee') ||
          (isPlanRelated && charge.concept === 'offline-pos-extra-location') ||
          (isPlanRelated &&
            charge.concept === 'offline-pos-location-upgrade')) &&
        (!isAppRelated || charge.appId === appId),
    );
  }, [charges, concept, appId]);
  const { translateGatewayPaymentMethod } = useTranslatePaymentMethod();

  const [getDownloadInvoice, { isLoading }] = useGetDownloadInvoiceMutation({
    fixedCacheKey: PaymentHistory.name,
  });

  const invoice = relevantCharges[0]?.invoice;

  const onDownloadInvoice = useCallback(() => {
    if (isLoading || !invoice) return;
    getDownloadInvoice(invoice.id)
      .unwrap()
      .then(({ url }) => {
        window.open(url, '_blank');
      });
  }, [getDownloadInvoice, invoice, isLoading]);

  const { getFrequencyDescriptor } = useFrequencyTranslation();
  const price = useMemo(
    () =>
      relevantCharges?.reduce(
        (acc, { amountValue }) => acc + Number(amountValue),
        0,
      ),
    [relevantCharges],
  );

  if (!paidAt || !relevantCharges.length) return null;
  const subscriptionData = relevantCharges[0].subscription;
  const isVariableCharge = relevantCharges[0]?.concept === 'app-extra-fee';
  const isPDVExtraLocation =
    relevantCharges[0]?.concept === 'offline-pos-extra-location';
  const isPDVUpgrade =
    relevantCharges[0]?.concept === 'offline-pos-location-upgrade';

  return (
    <>
      <hr />
      <Box
        paddingX="3"
        display="flex"
        flexDirection="row"
        justifyContent="space-between"
        paddingY="0-5"
      >
        <Box display="flex" flexDirection="column" gap="1-5">
          <Text>{dateFormat(paidAt, Format.DD_MM_YYYY, 'UTC')}</Text>
          <Text color="neutral-textLow">
            {translateGatewayPaymentMethod(gatewayDetail.paymentMethod)}
          </Text>
          {subscriptionData && !isVariableCharge && !isPDVExtraLocation && (
            <Text color="neutral-textLow">
              {`${t('subscription')} ${getFrequencyDescriptor(
                subscriptionData,
              )}`}
            </Text>
          )}
          {isVariableCharge && (
            <Text color="neutral-textLow">{t('extraFeeDescription')}</Text>
          )}
          {isPDVExtraLocation && (
            <Text color="neutral-textLow">
              {t('pdvExtraLocationDescription')}
            </Text>
          )}
          {isPDVUpgrade && (
            <Text color="neutral-textLow">{t('pdvUpgradeDescription')}</Text>
          )}
          {invoice?.status === 'AUTHORIZED' && (
            <Box
              color="primary-interactive"
              display="flex"
              flexDirection="row"
              gap="1"
              alignItems="center"
              onClick={onDownloadInvoice}
            >
              <InvoiceIcon size="small" />
              <Text color="primary-interactive">{t('downloadInvoice')}</Text>
            </Box>
          )}
        </Box>
        <CurrencyPrice price={price} currency={amountCurrency} />
      </Box>
    </>
  );
}
